import type { SponsorPlanItem } from '@app/_proto/Protos/sponsors';
import type { SubscriptionItem } from '@app/_proto/Protos/subscriptions';
import { SponsorPlanPlaceholder } from '@app/components/sponsors/sponsor.plan';
import ChampionPlanWithDialog from '@app/domains/champion/containers/ChampionPlanWithDialog';
import useMediaQueries from '@app/domains/common/hooks/useMediaQueries';
import clsx from 'clsx';
import { memo, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

type Props = {
    novelId: number | undefined;
    sponsorPlans?: SponsorPlanItem[];
    currentSubscription: SubscriptionItem | null;
    onSelectPlan?: (plan: SponsorPlanItem) => void;
    className?: string;
    gapX?: number;
    gapY?: number;
    maxColumn?: number;
    descLines?: number | undefined;
    showTierInfo?: boolean | undefined;
};

function ChampionPlans({
    novelId,
    sponsorPlans,
    currentSubscription,
    onSelectPlan,
    className,
    gapX = 12,
    gapY = 10,
    maxColumn = 3,
    descLines,
    showTierInfo,
}: Props) {
    const { isDownSm, isDownMd } = useMediaQueries();
    const [processing, setProcessing] = useState(false);
    const handleProcessingStateChange = useCallback((processingState: boolean) => {
        setProcessing(processingState);
    }, []);

    if (!novelId) {
        return null;
    }

    const params = {
        currentSubscription,
        onSelectPlan,
        handleProcessingStateChange,
        descLines,
        showTierInfo,
    };

    const widthStyleConds: [boolean, string][] = [
        [true, '100%'],
        [!isDownSm, `calc(50% - ${gapX}px)`],
        [!isDownMd, `calc(33.3% - ${(gapX * 2) / 2}px)`],
    ];

    const widthStyle = widthStyleConds.slice(0, maxColumn).reduce((acc, [condition, style]) => {
        return condition ? style : acc;
    }, '100%');

    return (
        <div
            className={twMerge(
                clsx('flex flex-wrap justify-center', {
                    'pointer-events-none': processing,
                }),
                className
            )}
            style={{ marginLeft: `-${gapX}px`, marginTop: `-${gapY}px` }}
        >
            {sponsorPlans?.map(plan => (
                <div
                    style={{ width: widthStyle, marginLeft: `${gapX}px`, marginTop: `${gapY}px` }}
                    className="flex justify-center"
                    key={plan.id}
                >
                    <ChampionPlanWithDialog plan={plan} {...params} />
                </div>
            ))}
            {!sponsorPlans &&
                Array(3)
                    .fill(null)
                    .map((_, idx) => (
                        <div className="mt-[15px] w-full px-[7.5px] sm:w-1/2 md:w-1/3" key={idx}>
                            <SponsorPlanPlaceholder />
                        </div>
                    ))}
        </div>
    );
}

export default memo(ChampionPlans);
